import React, { useMemo, useEffect, useRef } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import styled from 'styled-components';

import * as analytics from '@adsk/acs-web-analytics';
import { useCurrentToolId } from '@adsk/alloy-provider';
import { Box, Text } from '@adsk/alloy-react-box';
import Checkbox from '@adsk/alloy-react-checkbox';
import { keyBy } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import {
  useTable,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderGroup,
  LoadMoreDataFooter,
} from '@adsk/alloy-react-table';

import { useFormsContext } from './FormsProvider';
import {
  FORM_ANALYTICS,
  FORM_PLUGIN_METADATA,
  ENTITIES_MAPPER_KEY,
} from './helpers/constants';
import { ACTIONS } from './helpers/reducers/forms';
import { useVirtualTable, useTableColumns } from './hooks';
import { useTranslation } from './i18n';

const StyledTable = styled.table`
  width: ${(p) => p.width};
  border-spacing: 0;
  text-align: left;
`;

const StyledStickyThead = styled.thead`
  position: sticky;
  margin: 0;
  top: 0;
  z-index: 1;
`;

const FormsCustomDataView = ({
  entities,
  disabledIds,
  selectedIds,
  filters,
  onLoadMore,
  onChange,
  multiple,
  loading,
  error,
  setSelection,
  onTryAgain,
  dataViewProps,
}) => {
  const { dispatch } = useFormsContext();
  const toolId = useCurrentToolId();
  const tableColumns = useTableColumns();
  const { t } = useTranslation();
  useEffect(() => {
    toolId &&
      analytics.track(FORM_ANALYTICS.ADD, {
        reference_type: FORM_PLUGIN_METADATA.TYPE,
        source: toolId,
      });
  }, [toolId]);

  useEffect(() => {
    if (filters?.searchExpression) {
      analytics.track(FORM_ANALYTICS.SEARCH, {
        source: toolId,
        reference_type: FORM_PLUGIN_METADATA.TYPE,
        term: filters?.searchExpression,
      });
    }
  }, [toolId, filters]);

  useEffect(() => {
    dispatch({
      type: ACTIONS.UPDATE_FORM_TEMPLATE_CATEGORIES,
      payload: {
        templateCategories: dataViewProps?.templateCategories || [],
      },
    });
  }, [dispatch, dataViewProps]);

  // If the source entity is a form, disable it in the table
  const [disabledIdsWithSource, selectedIdsWithSource] = useMemo(
    () =>
      dataViewProps &&
      dataViewProps.sourceEntityType === FORM_PLUGIN_METADATA.TYPE &&
      dataViewProps.sourceEntityDomain === FORM_PLUGIN_METADATA.DOMAIN
        ? [
            [...disabledIds, dataViewProps.sourceEntityId],
            [...selectedIds, dataViewProps.sourceEntityId],
          ]
        : [disabledIds, selectedIds],
    [disabledIds, selectedIds, dataViewProps],
  );

  const rowSelection = useMemo(
    () =>
      selectedIdsWithSource?.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {}) ?? {},
    [selectedIdsWithSource],
  );

  // Keep refs in sync so the header onChange handler always reads the latest
  // values without needing them in the columns useMemo dependency array.
  // This prevents the entire column definition (and table header) from being
  // recreated on every individual row selection change.
  const selectedIdsWithSourceRef = useRef(selectedIdsWithSource);
  useEffect(() => {
    selectedIdsWithSourceRef.current = selectedIdsWithSource;
  }, [selectedIdsWithSource]);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const columns = useMemo(
    () => [
      ...(multiple
        ? [
            {
              id: 'select',
              size: 40,
              minSize: 40,
              header: ({ table: { getRowModel, getSelectedRowModel } }) => {
                const totalRows = getRowModel().rows.filter(
                  (row) => row.getCanSelect() || row.getIsSelected(),
                ).length;
                const selectedRows = getSelectedRowModel().rows.length;
                const isAllSelected = totalRows === selectedRows;
                const isSomeSelected =
                  selectedRows > 0 ? Checkbox.INDETERMINATE : false;
                return (
                  <Checkbox
                    data-testid="FormsHeaderSelectCheckbox"
                    checked={isAllSelected || isSomeSelected}
                    onChange={(checked) => {
                      // Preserve previously selected forms when toggling "select all"
                      entities.forEach((entity) => {
                        if (disabledIdsWithSource.includes(entity.id)) return;
                        const isSelected =
                          selectedIdsWithSourceRef.current.includes(entity.id);
                        if (checked && !isSelected) {
                          onChangeRef.current({ entity, checked: true });
                        } else if (!checked && isSelected) {
                          onChangeRef.current({ entity, checked: false });
                        }
                      });
                    }}
                    disabled={!multiple}
                  />
                );
              },
              cell: ({
                row: { getIsSomeSelected, getIsSelected, getCanSelect },
              }) => (
                <Checkbox
                  data-testid="FormsRowSelectCheckbox"
                  checked={
                    getIsSomeSelected()
                      ? Checkbox.INDETERMINATE
                      : getIsSelected()
                  }
                  disabled={!getCanSelect()}
                />
              ),
            },
          ]
        : []),
      ...tableColumns,
    ],
    [tableColumns, disabledIdsWithSource, entities, multiple],
  );

  const dataByKey = useMemo(
    () => keyBy(entities, ENTITIES_MAPPER_KEY),
    [entities],
  );

  const table = useReactTable({
    columns,
    data: entities,
    getRowId: (row) => row?.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => !disabledIdsWithSource?.includes(row.id),
    enableMultiRowSelection: multiple,
    enableColumnResizing: false,
    enableSortingRemoval: false,
    enableSorting: false,
    state: {
      rowSelection,
    },
  });

  const {
    getTableRowProps,
    getTableCellProps,
    getTableHeaderGroupProps,
    getTableHeaderProps,
  } = useTable({
    table,
  });

  const { getCenterTotalSize, getRowModel, getHeaderGroups } = table;

  const { rows } = getRowModel();

  const { handleScroll, virtualRows, layouts, tableRef } = useVirtualTable({
    onLoadMore,
    rowsLength: rows?.length,
    loading,
    error: !!error,
  });
  const { paddingTop, paddingBottom } = layouts;

  const handleRowSelection = ({ getCanSelect, getIsSelected, id }) => {
    if (!getCanSelect?.()) return;
    if (multiple) {
      onChange({
        entity: dataByKey[id],
        checked: !getIsSelected?.(),
      });
    } else {
      setSelection(selectedIdsWithSource.includes(id) ? [] : [dataByKey[id]]);
    }
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="auto"
      onScroll={handleScroll}
      ref={tableRef}
    >
      <StyledTable width={getCenterTotalSize()}>
        <StyledStickyThead>
          {getHeaderGroups()?.map((headerGroup) => (
            <TableHeaderGroup
              key={headerGroup.id}
              {...getTableHeaderGroupProps(headerGroup)}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} {...getTableHeaderProps(header)} />
              ))}
            </TableHeaderGroup>
          ))}
        </StyledStickyThead>
        <tbody>
          {paddingTop > 0 && <TableRow style={{ height: `${paddingTop}px` }} />}
          {virtualRows.map(({ index }) => {
            const row = rows[index];
            const { getVisibleCells, id, getCanSelect } = row;
            return (
              <TableRow
                key={id}
                {...getTableRowProps(row)}
                onClick={() => handleRowSelection(row)}
                disabled={!getCanSelect()}
              >
                {getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} {...getTableCellProps(cell)} />
                ))}
              </TableRow>
            );
          })}
          {paddingBottom > 0 && (
            <TableRow style={{ height: `${paddingBottom}px` }} />
          )}
        </tbody>
      </StyledTable>
      {(loading || error) && (
        <LoadMoreDataFooter
          isLoading={loading}
          onLoad={async () => onLoadMore?.()}
          hasError={!!error}
          onRetry={async () => onTryAgain?.()}
          style={{
            paddingLeft: '50px',
            justifyContent: 'flex-start',
          }}
          renderLoading={() => (
            <>
              <ProgressRing size={ProgressRing.SIZES.SMALL} />
              <Text
                typography="bodySmall"
                color="charcoal700"
                marginLeft="13px"
              >
                {t('LOADING_MORE')}
              </Text>
            </>
          )}
        />
      )}
    </Box>
  );
};

FormsCustomDataView.displayName = 'FormsCustomDataView';

export default FormsCustomDataView;
